import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Chart from "react-apexcharts";
import { toastEmitter } from "../../../utills/utilities";
import { API_RESPONSE } from "../../../utills/app-constant";
import { radialDimensionDetails } from "../../../services/api-services";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import DownloadGraphButton from "../../../user/pages/page/DownloadGraphButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RadialDimension = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const chartRef = useRef(null);
  const [rowData, setRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [isAttempt, setIsAttempt] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [dimensionNames, setDimensionNames] = useState([]);
  const [totalImportance, setTotalImportance] = useState(0);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalFuture, setTotalFuture] = useState(0);
  const [importanceValues, setImportanceValues] = useState([]);
  const [currentValues, setCurrentValues] = useState([]);
  const [futureValues, setFutureValues] = useState([]);

  const getRadialDimensionDetailsData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await radialDimensionDetails(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setRowData(response?.data);
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);
        const dimensions = response?.data?.responseBody || [];
        setDimensionNames(dimensions.map(d => d.dimensionName));
        setImportanceValues(dimensions.map(d => d.importantAverageValue));
        setCurrentValues(dimensions.map(d => d.currentAverageValue));
        setFutureValues(dimensions.map(d => d.futureAverageValue));
        setTotalImportance(response?.data?.totalImportanceAverage || 0);
        setTotalCurrent(response?.data?.totalCurrentAverage || 0);
        setTotalFuture(response?.data?.totalFutureAverage || 0);
      } else if (response?.data?.responseCode === 404) {
        setRowData([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName]);

  useEffect(() => {
    getRadialDimensionDetailsData();
  }, [getRadialDimensionDetailsData]);

  useEffect(() => {
    if ([...importanceValues, ...currentValues, ...futureValues].some(val => val > 0)) {
      setTimeout(() => {
        generateChartImage("radial_details", "radial_details", rowData?.remark);
      }, 500);
    }
  }, [importanceValues, currentValues, futureValues, rowData]);

  const chartData = useMemo(
    () => ({
      options: {
        chart: {
          id: "radial_details",
          type: "radar",
        },
        stroke: {
          width: 2,
          colors: ["#557D39", "#F7BB1C", "#D42327"],
        },
        fill: {
          opacity: 0,
          colors: ["#557D39", "#F7BB1C", "#D42327"],
        },
        markers: { size: 4 },
        tooltip: {
          y: {
            formatter: function (val) {
              return `${val?.toFixed(2)}%`;
            },
          },
        },
        yaxis: {
          labels: { formatter: (value) => value.toFixed(2) },
          barBorderColor: "#828282",
        },
        xaxis: { categories: dimensionNames },
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
      },
      series: [
        { name: "Current Score", data: currentValues },
        { name: "Future Score", data: futureValues },
        { name: "Importance Score", data: importanceValues },
      ],
    }),
    [dimensionNames, importanceValues, currentValues, futureValues]
  );


  const exportSummaryToExcel = (totalCurrent, totalFuture, totalImportance, fileName = "Radial Dimension details") => {
    const heading = ["Radial Dimension details"];
    const summaryRows = [
      heading,
      [], // empty row for spacing (optional)
      [`Total Current = ${totalCurrent.toFixed(2)}`],
      [`Total Future = ${totalFuture.toFixed(2)}`],
      [`Total Importance = ${totalImportance.toFixed(2)}`],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "summaryRows");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <div className="RadialDimention_Sec">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between">
            <h5>Radial</h5>
            <div className="d-flex">
              <button className="mb-3 dbtn px-3 py-2 text-white bg-primary rounded border-0 cursor-pointer me-3" onClick={() => exportSummaryToExcel(totalCurrent, totalFuture, totalImportance)}>Download Excel</button>
              <DownloadGraphButton chartID="chart3" myhandleExport={myhandleExport} />
            </div>

          </div>

          <div className="chart3" data-heading="Radial details" data-remark={remark}>
            <Chart options={chartData.options} series={chartData.series} type="radar" height={450} ref={chartRef} />
          </div>
          <div className="d-flex justify-content-around rounded my-3 ">
            <div className="text-center ">
              <h6 className="textfont12">Total Current</h6>
              <h5 className="text-success textfont13">{totalCurrent.toFixed(2)}</h5>
            </div>
            <div className="text-center ">
              <h6 className="textfont12">Total Future</h6>
              <h5 className="text-warning textfont13">{totalFuture.toFixed(2)}</h5>
            </div>
            <div className="text-center">
              <h6 className="textfont12">Total Importance</h6>
              <h5 className="text-danger textfont13">{totalImportance.toFixed(2)}</h5>
            </div>
          </div>
        </>
      )}

      <Remark
        graphName="RadialDimensiondetails"
        selectedCompanyId={selectedCompanyId}
        initialRemark={remark}
        remarkId={remarkId}
        isAttempt={isAttempt}
        setRemarkprops={setRemark}
      />
    </div>
  );
};

export default RadialDimension;
